/**
 * Extracts translation keys from a question definition's UI section (and adminDescription).
 * Keys are used by assessment components with t(key) from 'assessment' namespace.
 *
 * Based on usage in @awarego-ui/packages/components questions:
 * - presentation: title, pretitle, description (illustration, presentation-heading, question, message)
 * - blocks[].title, pretitle, description (question-heading)
 * - blocks[].options[].text (selects-group, question-with-image, numerical-scale, etc.)
 * - blocks[].answer.heading[].text, .prefix (block-answer)
 * - blocks[].answer.description_key (block-answer)
 * - blocks[].answer.details.header, details.items[].text, .description (block-answer)
 * - blocks[].answer (passwordCriteria[].text, etc.)
 * - presentation.points[].text (interactive-img)
 * - email: text, from_name, subject, link.text, attachment.text, footer.text, etc.
 * - adminDescription (LMS description, not rendered via t() but often a key or free text)
 */

const TRANSLATION_KEY_FIELDS = [
    'title',
    'pretitle',
    'description',
    'text',
    'prefix',
    'description_key',
    'header',
    'from_name',
    'from_initials',
    'from_address',
    'subject',
    'to_name',
    'to_address',
    'extra_to_string',
];

function collectStringsFromValue(value, out) {
    if (value == null) return;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) out.add(trimmed);
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item) => collectStringsFromValue(item, out));
        return;
    }
    if (typeof value === 'object') {
        Object.keys(value).forEach((key) => {
            if (TRANSLATION_KEY_FIELDS.includes(key) || key === 'email') {
                collectStringsFromValue(value[key], out);
            } else {
                // Recurse into nested objects that might contain translation keys (e.g. details.items)
                collectStringsFromValue(value[key], out);
            }
        });
    }
}

/**
 * @param {string|object} definition - Question definition JSON string or parsed object
 * @returns {string[]} Sorted, unique list of translation keys referenced in the definition
 */
export function questionDefinitionToTranslationKeys(definition) {
    const out = new Set();
    let def;
    if (typeof definition === 'string') {
        try {
            def = JSON.parse(definition);
        } catch {
            return [];
        }
    } else if (definition && typeof definition === 'object') {
        def = definition;
    } else {
        return [];
    }

    if (def.ui) {
        collectStringsFromValue(def.ui, out);
    }

    return [...out].sort();
}
